import React, { useState, useEffect, useRef } from 'react';
const API_URL = 'api';
import { Box, Paper, IconButton, Typography, Slider, Stack, Button, Grid, AppBar, Toolbar, Tooltip, TextField, Divider, Dialog, DialogTitle, DialogContent, DialogActions, Card, CardMedia, CircularProgress } from '@mui/material';
import { PlayArrow, Pause, Add, Save, ZoomIn, ZoomOut, ArrowBack, Delete, Layers, CloudUpload, Language, ChevronLeft, ChevronRight, Settings, Dashboard, ViewList, FolderSpecial } from '@mui/icons-material';
import { Rnd } from 'react-rnd';
import axios from 'axios';
import GlassContainer from './components/GlassContainer';

const TimelineEditor = ({ onBack, layoutId, onIdCreated }) => {
    // --- State ---
    const [name, setName] = useState('Nuevo Diseño');
    const [currentTime, setCurrentTime] = useState(0); // in seconds
    const [isPlaying, setIsPlaying] = useState(false);
    const [zoom, setZoom] = useState(20); // pixels per second

    // items: [{ id, type, src, name, start, duration, layer, x, y, w, h, z, visible, locked }]
    const [items, setItems] = useState([]);
    const [snapLine, setSnapLine] = useState(null); // Pixel value for snap guide

    const [selectedId, setSelectedId] = useState(null);
    const canvasRef = useRef(null);
    const [showMediaModal, setShowMediaModal] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [showWebModal, setShowWebModal] = useState(false);
    const [showPlaylistModal, setShowPlaylistModal] = useState(false);
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [resolution, setResolution] = useState({ width: 1920, height: 1080 });
    const [activeTab, setActiveTab] = useState(0); // 0: Visual (Canvas), 1: Timeline
    const [canvasSize, setCanvasSize] = useState({ width: 800, height: 450 });

    // --- Resize Observer for Visual Editor ---
    useEffect(() => {
        if (!canvasRef.current) return;
        const resizeObserver = new ResizeObserver(entries => {
            for (let entry of entries) {
                const { width, height } = entry.contentRect;
                setCanvasSize({ width, height });
            }
        });
        resizeObserver.observe(canvasRef.current);
        return () => resizeObserver.disconnect();
    }, [activeTab]); // Re-bind when switching tabs ensures we catch the layout

    // Calculate Project Duration Dynamically (Max Item End)
    const projectDuration = items.length > 0 ? Math.max(...items.map(i => i.start + i.duration)) : 60;

    // Load layout if ID provided
    useEffect(() => {
        if (layoutId && layoutId !== 'new') {
            axios.get(`${API_URL}/layouts/${layoutId}`).then(res => {
                setName(res.data.name);
                setItems(res.data.zones || []);
                if (res.data.resolution) setResolution(res.data.resolution);
                // Resets
                setCurrentTime(0);
            }).catch(err => alert("Error al cargar layout: " + err.message));
        } else {
            // New Layout: Inject default news widget
            setItems([{
                id: 'news-' + Date.now(),
                type: 'web',
                src: 'https://barranoticiasauto.acciotvservernoticias.com/',
                name: 'Barra de Noticias (Auto)',
                start: 0,
                duration: 60, // Initial duration, will be synced
                layer: 3,
                x: 0, y: 88, w: 100, h: 12, z: 3,
                visible: true, locked: false
            }]);
            setName('Nueva Composición');
        }
    }, [layoutId]);

    // --- News Widget Auto-Duration Sync ---
    useEffect(() => {
        const layer0Items = items.filter(i => i.layer === 0);
        const playlistDuration = layer0Items.reduce((max, i) => Math.max(max, i.start + i.duration), 0);
        const targetDuration = playlistDuration || 60;

        const newsWidget = items.find(i => i.src === 'https://barranoticiasauto.acciotvservernoticias.com/');
        if (newsWidget && Math.abs(newsWidget.duration - targetDuration) > 0.1) {
            setItems(prev => prev.map(i => i.id === newsWidget.id ? { ...i, duration: targetDuration } : i));
        }
    }, [items]);

    // --- Magnetic Timeline Logic (Layer 0 = Playlist) ---
    useEffect(() => {
        // Separate Main Track (Layer 0) from Overlays (Layers 1-3)
        const mainTrack = items.filter(i => i.layer === 0).sort((a, b) => a.start - b.start);
        const overlays = items.filter(i => i.layer !== 0);

        let changed = false;
        let cumulativeTime = 0;

        const newMainTrack = mainTrack.map(item => {
            // Force sequential start times
            if (Math.abs(item.start - cumulativeTime) > 0.05) { // 50ms tolerance
                changed = true;
                return { ...item, start: cumulativeTime };
            }
            cumulativeTime += item.duration;
            return item;
        });

        // Only trigger update if something actually moved to avoid infinite loops
        if (changed) {
            setItems([...newMainTrack, ...overlays]);
        }
    }, [items]); // Dependency on items ensures continuous enforcement

    // --- Playback Loop (Smooth requestAnimationFrame) ---
    useEffect(() => {
        let rafId;
        let lastTime = performance.now();

        const tick = (now) => {
            if (isPlaying) {
                const delta = (now - lastTime) / 1000;
                lastTime = now;
                setCurrentTime(t => {
                    const next = t + delta;
                    return next >= projectDuration ? 0 : next;
                });
                rafId = requestAnimationFrame(tick);
            }
        };

        if (isPlaying) {
            lastTime = performance.now();
            rafId = requestAnimationFrame(tick);
        }

        return () => {
            if (rafId) cancelAnimationFrame(rafId);
        };
    }, [isPlaying, projectDuration]);

    // --- Zoom Logic ---
    const handleZoomChange = (newZoom) => {
        const container = document.getElementById('full-timeline-scroll');
        if (!container) {
            setZoom(newZoom);
            return;
        }

        // 1. Calculate the time currently at the *center* of the viewport
        const viewportWidth = container.clientWidth;
        const scrollLeft = container.scrollLeft;
        const centerTime = (scrollLeft + viewportWidth / 2) / zoom;

        // 2. Update Zoom State
        setZoom(newZoom);

        // 3. Adjust Scroll to keep that time at the center
        // We use requestAnimationFrame to wait for the DOM layout update (width change)
        requestAnimationFrame(() => {
            const newScrollLeft = (centerTime * newZoom) - (viewportWidth / 2);
            container.scrollLeft = Math.max(0, newScrollLeft);
        });
    };

    // --- Keyboard Shortcuts ---
    useEffect(() => {
        const handleKeyDown = (e) => {
            // Ignore if typing in an input
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

            switch (e.key) {
                case ' ': // Spacebar -> Play/Pause
                    e.preventDefault();
                    setIsPlaying(p => !p);
                    break;
                case 'Delete':
                case 'Backspace': // Delete -> Remove Item
                    if (selectedId) {
                        setItems(prev => prev.filter(i => i.id !== selectedId));
                        setSelectedId(null);
                    }
                    break;
                case 'ArrowLeft': // Nudge Playhead
                    setCurrentTime(t => Math.max(0, t - 0.1));
                    break;
                case 'ArrowRight':
                    setCurrentTime(t => Math.min(projectDuration, t + 0.1));
                    break;
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [selectedId, projectDuration]);

    // --- Helpers ---
    const activeItems = items.filter(i => i.visible && currentTime >= i.start && currentTime <= (i.start + i.duration));
    const handleSeek = (e, val) => { setCurrentTime(val); setIsPlaying(false); };

    const handleSave = () => {
        const payload = {
            name: name,
            description: "Composición pro",
            resolution: resolution,
            zones: items
        };
        const req = (layoutId && layoutId !== 'new')
            ? axios.put(`${API_URL}/layouts/${layoutId}`, payload)
            : axios.post(`${API_URL}/layouts`, payload);

        req.then(res => {
            alert('Guardado con éxito!');
            if (res.data.id && onIdCreated) onIdCreated(res.data.id);
        }).catch(err => alert('Error al guardar: ' + err.message));
    };

    const updateItem = (id, changes) => {
        setItems(prevItems => prevItems.map(i => i.id === id ? { ...i, ...changes } : i));
    };

    const handleRender = () => {
        setIsPlaying(false);
        const composition = {
            duration: projectDuration,
            video_tracks: items.filter(i => i.type === 'video').map(i => ({
                filename: i.src, start: i.start, duration: i.duration,
                x: i.x, y: i.y, w: i.w, h: i.h
            }))
        };
        axios.post(`${API_URL}/render`, { composition })
            .then(res => alert(`Renderizado iniciado! Job ID: ${res.data.jobId}`))
            .catch(err => alert('Error al iniciar render: ' + err.message));
    };

    return (
        <Box sx={{ height: 'calc(100vh - 10px)', display: 'flex', flexDirection: 'column', color: 'white', overflow: 'hidden', bgcolor: '#05050a' }}>

            {/* Header Toolbar */}
            <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 2, borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                <IconButton onClick={onBack} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.05)' }}><ArrowBack /></IconButton>
                <Box sx={{ flexGrow: 1 }}>
                    <TextField
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        variant="standard"
                        placeholder="NOMBRE DEL PROYECTO"
                        InputProps={{ disableUnderline: true, sx: { fontSize: '1.2rem', fontWeight: 900, color: 'white' } }}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, opacity: 0.6 }}>
                        {/* Calculated Duration Display */}
                        <Typography variant="caption">
                            DURACIÓN: {projectDuration.toFixed(1)}s (AUTO)
                        </Typography>
                        <Typography variant="caption">• {resolution.width}x{resolution.height}</Typography>
                    </Box>
                </Box>

                {/* View Tabs */}
                <Box sx={{ bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 2, p: 0.5, display: 'flex' }}>
                    <Button
                        size="small"
                        variant={activeTab === 0 ? 'contained' : 'text'}
                        onClick={() => setActiveTab(0)}
                        startIcon={<Dashboard />}
                        sx={{ borderRadius: 1.5 }}
                    >
                        VISUAL
                    </Button>
                    <Button
                        size="small"
                        variant={activeTab === 1 ? 'contained' : 'text'}
                        onClick={() => setActiveTab(1)}
                        startIcon={<ViewList />}
                        sx={{ borderRadius: 1.5 }}
                    >
                        TIMELINE
                    </Button>
                </Box>

                <Stack direction="row" spacing={1}>
                    <IconButton onClick={() => setShowSettingsModal(true)} sx={{ border: '1px solid rgba(255,255,255,0.1)', borderRadius: 1 }}><Settings /></IconButton>
                    <Button variant="outlined" color="inherit" size="small" startIcon={<Add />} onClick={() => setShowMediaModal(true)}>MEDIA</Button>
                    <Button variant="outlined" color="info" size="small" startIcon={<Language />} onClick={() => setShowWebModal(true)}>WEB</Button>
                    <Button variant="outlined" color="secondary" size="small" startIcon={<ViewList />} onClick={() => setShowPlaylistModal(true)}>PLAYLIST</Button>
                    <Button variant="contained" size="small" startIcon={<Save />} onClick={handleSave}>GUARDAR</Button>
                    <Button variant="contained" color="secondary" size="small" startIcon={<CloudUpload />} onClick={handleRender}>EXPORTAR</Button>
                </Stack>
            </Box>

            {/* Main Workspace */}
            <Box sx={{ flexGrow: 1, display: 'flex', overflow: 'hidden', position: 'relative' }}>

                {/* Visual View (Canvas + Properties) */}
                <Box sx={{
                    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                    display: activeTab === 0 ? 'flex' : 'none',
                    zIndex: 1
                }}>
                    {/* Layers Panel */}
                    <Box sx={{
                        width: sidebarOpen ? 280 : 50, transition: 'width 0.2s',
                        borderRight: '1px solid rgba(255,255,255,0.05)', display: 'flex', flexDirection: 'column', bgcolor: 'rgba(0,0,0,0.2)'
                    }}>
                        <Box sx={{ p: 1, borderBottom: '1px solid rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: sidebarOpen ? 'space-between' : 'center' }}>
                            {sidebarOpen && <Typography variant="overline" sx={{ fontWeight: 900, opacity: 0.5 }}>CAPAS</Typography>}
                            <IconButton size="small" onClick={() => setSidebarOpen(!sidebarOpen)}>
                                {sidebarOpen ? <ChevronLeft /> : <ChevronRight />}
                            </IconButton>
                        </Box>
                        {sidebarOpen && (
                            <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 1 }}>
                                <Stack spacing={0.5}>
                                    {items.sort((a, b) => b.z - a.z).map(item => (
                                        <Box key={item.id} onClick={() => setSelectedId(item.id)} sx={{
                                            p: 1.5, borderRadius: 2, cursor: 'pointer',
                                            bgcolor: selectedId === item.id ? 'rgba(59, 130, 246, 0.15)' : 'transparent',
                                            border: selectedId === item.id ? '1px solid #3b82f6' : '1px solid transparent',
                                            display: 'flex', alignItems: 'center', gap: 1.5,
                                            '&:hover': { bgcolor: selectedId === item.id ? 'rgba(59, 130, 246, 0.2)' : 'rgba(255,255,255,0.02)' }
                                        }}>
                                            <Box sx={{
                                                width: 32, height: 32, borderRadius: 1,
                                                bgcolor: item.type === 'video' ? '#3b82f6' : item.type === 'image' ? '#10b981' : item.type === 'playlist' ? '#8b5cf6' : '#f59e0b',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 900
                                            }}>
                                                {item.type === 'video' ? 'MOV' : item.type === 'image' ? 'IMG' : item.type === 'playlist' ? 'PLY' : 'WEB'}
                                            </Box>
                                            <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                                                <Typography variant="body2" noWrap sx={{ fontWeight: 700 }}>{item.name}</Typography>
                                                <Typography variant="caption" sx={{ opacity: 0.4 }}>Z-INDEX: {item.z}</Typography>
                                            </Box>
                                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); updateItem(item.id, { visible: !item.visible }); }} sx={{ opacity: item.visible ? 1 : 0.2 }}><Layers sx={{ fontSize: 16 }} /></IconButton>
                                        </Box>
                                    ))}
                                </Stack>
                            </Box>
                        )}
                    </Box>

                    {/* Canvas Area */}
                    <Box sx={{ flexGrow: 1, position: 'relative', display: 'flex', flexDirection: 'column', bgcolor: '#000' }}>
                        <Box sx={{
                            flexGrow: 1, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            backgroundImage: 'radial-gradient(rgba(255,255,255,0.05) 1px, transparent 1px)',
                            backgroundSize: '30px 30px'
                        }}>
                            <Box
                                ref={canvasRef} // Use Ref for Observer
                                id="canvas-container"
                                sx={{
                                    width: '85%', aspectRatio: `${resolution.width}/${resolution.height}`, bgcolor: '#0a0a0a', position: 'relative',
                                    boxShadow: '0 0 100px rgba(0,0,0,1)', border: '1px solid #222'
                                }}
                            >
                                {activeItems.map(item => (
                                    <Rnd
                                        key={item.id}
                                        size={{ width: `${item.w}%`, height: `${item.h}%` }}
                                        position={{
                                            x: (item.x / 100) * (canvasSize.width || 800),
                                            y: (item.y / 100) * (canvasSize.height || 450)
                                        }}
                                        onDragStop={(e, d) => {
                                            // Update % based on CURRENT tracked size
                                            updateItem(item.id, {
                                                x: (d.x / canvasSize.width) * 100,
                                                y: (d.y / canvasSize.height) * 100
                                            });
                                        }}
                                        onResizeStop={(e, dir, ref, delta, pos) => {
                                            updateItem(item.id, {
                                                w: (ref.offsetWidth / canvasSize.width) * 100,
                                                h: (ref.offsetHeight / canvasSize.height) * 100,
                                                x: (pos.x / canvasSize.width) * 100,
                                                y: (pos.y / canvasSize.height) * 100
                                            });
                                        }}
                                        bounds="parent"
                                        style={{ border: selectedId === item.id ? '2px solid #3b82f6' : '1px solid rgba(255,255,255,0.1)', zIndex: item.z }}
                                        onClick={(e) => { e.stopPropagation(); setSelectedId(item.id); }}
                                    >
                                        <Box sx={{ width: '100%', height: '100%', position: 'relative', bgcolor: 'black' }}>
                                            {item.type === 'video' && (
                                                <video
                                                    src={`uploads/${item.src}`}
                                                    style={{ width: '100%', height: '100%', objectFit: 'fill' }}
                                                    ref={el => {
                                                        if (el && isPlaying) {
                                                            const offset = currentTime - item.start;
                                                            if (Math.abs(el.currentTime - offset) > 0.5) el.currentTime = offset;
                                                            el.play().catch(() => { });
                                                        } else if (el) { el.pause(); el.currentTime = currentTime - item.start; }
                                                    }}
                                                    muted
                                                />
                                            )}
                                            {item.type === 'image' && <img src={`uploads/${item.src}`} style={{ width: '100%', height: '100%', objectFit: 'fill' }} />}
                                            {(item.type === 'html' || item.type === 'web') && <iframe src={item.src} style={{ width: '100%', height: '100%', border: 'none', pointerEvents: 'none' }} />}
                                            {item.type === 'playlist' && (
                                                <NestedPlaylistPlayer item={item} currentTime={currentTime} isPlaying={isPlaying} />
                                            )}
                                        </Box>
                                    </Rnd>
                                ))}
                            </Box>

                            {/* Floating Playback Controls (Visual Mode) */}
                            <Paper sx={{
                                position: 'absolute', bottom: 30, display: 'flex', alignItems: 'center', gap: 3, p: 1.5, px: 3,
                                borderRadius: 50, bgcolor: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.1)'
                            }}>
                                <IconButton color="primary" onClick={() => setIsPlaying(!isPlaying)}>{isPlaying ? <Pause /> : <PlayArrow />}</IconButton>
                                <Box sx={{ minWidth: 80, textAlign: 'center' }}>
                                    <Typography variant="caption" sx={{ opacity: 0.5, display: 'block' }}>PLAYHEAD</Typography>
                                    <Typography variant="body2" fontFamily="monospace" fontWeight={900}>{currentTime.toFixed(1)}s</Typography>
                                </Box>
                                <Slider value={currentTime} max={projectDuration} step={0.1} onChange={handleSeek} sx={{ width: 200 }} />
                            </Paper>
                        </Box>
                    </Box>

                    {/* Properties Panel */}
                    <Box sx={{ width: 320, borderLeft: '1px solid rgba(255,255,255,0.05)', p: 2, overflowY: 'auto', bgcolor: 'rgba(0,0,0,0.2)' }}>
                        <Typography variant="overline" sx={{ fontWeight: 900, opacity: 0.5 }}>PROPIEDADES</Typography>
                        {selectedId ? (() => {
                            const item = items.find(i => i.id === selectedId);
                            if (!item) return null;
                            return (
                                <Stack spacing={3} sx={{ mt: 2 }}>
                                    <TextField label="NOMBRE" fullWidth size="small" value={item.name} onChange={e => updateItem(item.id, { name: e.target.value })} />
                                    <Box>
                                        <Typography variant="caption" sx={{ fontWeight: 800, color: '#3b82f6', mb: 1, display: 'block' }}>GEOMETRÍA (%)</Typography>
                                        <Grid container spacing={1}>
                                            <Grid item xs={6}><TextField label="X" type="number" size="small" value={item.x} onChange={e => updateItem(item.id, { x: parseFloat(e.target.value) })} /></Grid>
                                            <Grid item xs={6}><TextField label="Y" type="number" size="small" value={item.y} onChange={e => updateItem(item.id, { y: parseFloat(e.target.value) })} /></Grid>
                                            <Grid item xs={6}><TextField label="ANCHO" type="number" size="small" value={item.w} onChange={e => updateItem(item.id, { w: parseFloat(e.target.value) })} /></Grid>
                                            <Grid item xs={6}><TextField label="ALTO" type="number" size="small" value={item.h} onChange={e => updateItem(item.id, { h: parseFloat(e.target.value) })} /></Grid>
                                        </Grid>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" sx={{ fontWeight: 800, color: '#3b82f6', mb: 1, display: 'block' }}>TIEMPOS (s)</Typography>
                                        <Grid container spacing={1}>
                                            <Grid item xs={6}><TextField label="INICIO" type="number" size="small" value={item.start} onChange={e => updateItem(item.id, { start: parseFloat(e.target.value) })} /></Grid>
                                            <Grid item xs={6}>
                                                <TextField
                                                    label={item.type === 'video' ? 'CLIP' : 'DURACIÓN'}
                                                    type="number" size="small" value={item.duration}
                                                    onChange={e => updateItem(item.id, { duration: parseFloat(e.target.value) })}
                                                />
                                            </Grid>
                                        </Grid>
                                        {item.type === 'video' && item.sourceDuration && (
                                            <Typography variant="caption" sx={{ opacity: 0.35, mt: 0.5, display: 'block' }}>
                                                Duración original: {item.sourceDuration}s
                                            </Typography>
                                        )}
                                        {item.type === 'playlist' && (
                                            <Typography variant="caption" sx={{ opacity: 0.35, mt: 0.5, display: 'block', color: '#8b5cf6' }}>
                                                Hereda contenido del Playlist maestro
                                            </Typography>
                                        )}
                                        {item.type === 'image' && (
                                            <Typography variant="caption" sx={{ opacity: 0.35, mt: 0.5, display: 'block' }}>
                                                Tiempo visible en pantalla
                                            </Typography>
                                        )}
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" sx={{ fontWeight: 800, color: '#3b82f6', mb: 1, display: 'block' }}>ORDEN VISUAL (Z)</Typography>
                                        <TextField select fullWidth size="small" value={item.z} onChange={e => updateItem(item.id, { z: parseInt(e.target.value) })} SelectProps={{ native: true }}>
                                            <option value={0}>Fondo (0)</option>
                                            <option value={1}>Capa 1</option>
                                            <option value={2}>Capa 2</option>
                                            <option value={3}>Frente (3)</option>
                                        </TextField>
                                    </Box>
                                    <Button fullWidth variant="outlined" color="error" startIcon={<Delete />} onClick={() => { setItems(items.filter(i => i.id !== selectedId)); setSelectedId(null); }}>ELIMINAR CAPA</Button>
                                </Stack>
                            );
                        })() : (
                            <Box sx={{ mt: 10, textAlign: 'center', opacity: 0.2 }}>
                                <Layers sx={{ fontSize: 60, mb: 2 }} />
                                <Typography variant="body2">No hay Selección</Typography>
                            </Box>
                        )}
                    </Box>
                </Box>

                {/* Timeline View (Storyboard) */}
                <Box sx={{
                    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                    display: activeTab === 1 ? 'flex' : 'none', flexDirection: 'column',
                    zIndex: 1, bgcolor: '#0a0a10', p: 1
                }}>
                    <Box sx={{
                        flexGrow: 1,
                        border: '1px solid rgba(255,255,255,0.05)',
                        borderRadius: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        bgcolor: 'rgba(5, 5, 10, 0.5)'
                    }}>
                        {/* 1. Header Bar */}
                        <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 2, borderBottom: '1px solid rgba(255,255,255,0.05)', bgcolor: 'rgba(255,255,255,0.02)' }}>
                            <Typography variant="overline" sx={{ fontWeight: 800, opacity: 0.6 }}>GUION GRÁFICO (STORYBOARD)</Typography>
                        </Box>

                        {/* 2. Storyboard Filmstrip Container */}
                        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', bgcolor: '#05050a', overflow: 'hidden' }}>
                            {/* Main Playlist (Layer 0) - Horizontal Filmstrip */}
                            <Box sx={{ p: 2, borderBottom: '1px solid rgba(255,255,255,0.05)', bgcolor: 'rgba(59, 130, 246, 0.02)' }}>
                                <Typography variant="overline" sx={{ fontWeight: 900, color: '#3b82f6', mb: 1, display: 'block' }}>CANAL PRINCIPAL (PLAYLIST)</Typography>
                                <Box sx={{
                                    display: 'flex', gap: 2, overflowX: 'auto', pb: 1, minHeight: 120,
                                    '&::-webkit-scrollbar': { height: 6 },
                                    '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(59, 130, 246, 0.2)', borderRadius: 10 }
                                }}>
                                    {items.filter(i => i.layer === 0).sort((a, b) => a.start - b.start).map((item, idx) => (
                                        <Paper key={item.id} sx={{
                                            minWidth: 160, width: 160, height: 90, bgcolor: '#0f172a', borderRadius: 2,
                                            border: selectedId === item.id ? '2px solid #3b82f6' : '1px solid rgba(255,255,255,0.1)',
                                            position: 'relative', cursor: 'pointer', overflow: 'hidden',
                                            transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.02)' }
                                        }} onClick={() => setSelectedId(item.id)}>
                                            <Box sx={{ position: 'absolute', top: 5, left: 5, zIndex: 5, px: 0.8, py: 0.2, bgcolor: 'rgba(0,0,0,0.6)', borderRadius: 1 }}>
                                                <Typography variant="caption" sx={{ fontWeight: 900, fontSize: '9px' }}>#{idx + 1}</Typography>
                                            </Box>
                                            {item.type === 'image' && <img src={`uploads/${item.src}`} style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.5 }} />}
                                            {item.type === 'video' && <Box sx={{ width: '100%', height: '100%', background: 'linear-gradient(45deg, #1e3a8a, #0f172a)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><PlayArrow sx={{ opacity: 0.3 }} /></Box>}
                                            {item.type === 'playlist' && <Box sx={{ width: '100%', height: '100%', background: 'linear-gradient(45deg, #3b0764, #4c1d95)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><ViewList sx={{ opacity: 0.3 }} /></Box>}
                                            <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, p: 0.5, bgcolor: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}>
                                                <Typography variant="caption" noWrap sx={{ display: 'block', fontSize: '9px', fontWeight: 700 }}>{item.name}</Typography>
                                                <Typography variant="caption" sx={{ fontSize: '8px', opacity: 0.5 }}>{item.duration.toFixed(1)}s</Typography>
                                            </Box>
                                        </Paper>
                                    ))}
                                    <Box sx={{
                                        minWidth: 160, height: 90, border: '2px dashed rgba(255,255,255,0.05)', borderRadius: 2,
                                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                        cursor: 'pointer', '&:hover': { bgcolor: 'rgba(255,255,255,0.02)', borderColor: '#3b82f6' }
                                    }} onClick={() => setShowMediaModal(true)}>
                                        <Add sx={{ opacity: 0.3 }} />
                                        <Typography variant="caption" sx={{ opacity: 0.3, fontWeight: 700 }}>AÑADIR CLIP</Typography>
                                    </Box>
                                </Box>
                            </Box>

                            {/* Overlays & Misc Section */}
                            <Box sx={{ flexGrow: 1, p: 2, overflowY: 'auto' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="overline" sx={{ fontWeight: 900, opacity: 0.6 }}>ELEMENTOS SOBREPUESTOS (WIDGETS)</Typography>
                                    <Button size="small" startIcon={<Language />} onClick={() => setShowWebModal(true)} sx={{ color: '#3b82f6' }}>AÑADIR WEB</Button>
                                </Box>
                                <Grid container spacing={2}>
                                    {items.filter(i => i.layer !== 0).map(item => (
                                        <Grid item xs={12} key={item.id}>
                                            <Paper sx={{
                                                p: 1.5, bgcolor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)',
                                                borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2,
                                                borderColor: selectedId === item.id ? '#3b82f6' : 'rgba(255,255,255,0.05)'
                                            }} onClick={() => setSelectedId(item.id)}>
                                                <Box sx={{ width: 40, height: 40, bgcolor: '#1e3a8a', borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    {item.type === 'web' ? <Language fontSize="small" /> : <FolderSpecial fontSize="small" />}
                                                </Box>
                                                <Box sx={{ flexGrow: 1 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 800 }}>{item.name}</Typography>
                                                    <Typography variant="caption" sx={{ opacity: 0.5 }}>Capa {item.layer} • {item.duration}s</Typography>
                                                </Box>
                                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); setItems(items.filter(i => i.id !== item.id)); }} sx={{ color: '#ef4444' }}><Delete fontSize="small" /></IconButton>
                                            </Paper>
                                        </Grid>
                                    ))}
                                    {items.filter(i => i.layer !== 0).length === 0 && (
                                        <Grid item xs={12}>
                                            <Box sx={{ p: 3, textAlign: 'center', border: '1px dashed rgba(255,255,255,0.05)', borderRadius: 2 }}>
                                                <Typography variant="caption" sx={{ opacity: 0.3 }}>No hay elementos sobrepuestos</Typography>
                                            </Box>
                                        </Grid>
                                    )}
                                </Grid>
                            </Box>
                        </Box>

                        {/* 3. Transport Controls */}
                        <Paper sx={{ m: 1, display: 'flex', alignItems: 'center', gap: 3, p: 1, px: 2, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
                            <IconButton color="primary" onClick={() => setIsPlaying(!isPlaying)} sx={{ bgcolor: 'rgba(59, 130, 246, 0.1)' }}>{isPlaying ? <Pause /> : <PlayArrow />}</IconButton>
                            <Box sx={{ minWidth: 100 }}>
                                <Typography variant="caption" sx={{ opacity: 0.4, display: 'block', mb: -0.5 }}>POSICIÓN</Typography>
                                <Typography variant="body2" fontFamily="monospace" fontWeight={900} sx={{ color: '#3b82f6' }}>{currentTime.toFixed(2)}s</Typography>
                            </Box>
                            <Slider value={currentTime} max={projectDuration} step={0.01} onChange={handleSeek} sx={{ flexGrow: 1 }} />
                        </Paper>
                    </Box>
                </Box>

                {/* Media Selector Dialog */}
                <MediaSelectDialog open={showMediaModal} onClose={() => setShowMediaModal(false)} onSelect={(m) => {
                    const baseDuration = m.duration || 10;
                    // Place after the last item on LAYER 0 (Bottom/Main Track)
                    const layer0Items = items.filter(i => i.layer === 0);
                    const lastEnd = layer0Items.reduce((max, i) => Math.max(max, i.start + i.duration), 0);
                    // Ensure no gaps: Snap perfectly to last end
                    const startAt = lastEnd; // Even if 0 items, lastEnd is 0

                    const newItem = {
                        id: Date.now(), type: m.type, src: m.filename, name: m.original_name,
                        start: startAt, duration: baseDuration, layer: 0,
                        x: 0, y: 0, w: 100, h: 100, z: 0, visible: true, locked: false,
                        sourceDuration: baseDuration
                    };

                    // For videos, probe actual duration from the file
                    if (m.type === 'video') {
                        const probe = document.createElement('video');
                        probe.preload = 'metadata';
                        probe.src = `/uploads/${m.filename}`;
                        probe.onloadedmetadata = () => {
                            const realDur = Math.round(probe.duration * 10) / 10;
                            setItems(prev => {
                                const exists = prev.find(p => p.id === newItem.id);
                                if (!exists) return prev;

                                const oldDur = exists.duration;
                                const diff = realDur - oldDur;

                                // RIPPLE: Shift all items on the same layer that started after this one
                                return prev.map(p => {
                                    if (p.id === newItem.id) {
                                        return { ...p, duration: realDur, sourceDuration: realDur };
                                    }
                                    if (p.layer === exists.layer && p.start >= (exists.start + oldDur - 0.1)) {
                                        // It was effectively after. Shift it.
                                        return { ...p, start: p.start + diff };
                                    }
                                    return p;
                                });
                            });
                            probe.remove();
                        };
                    }

                    setItems(prev => [...prev, newItem]); setShowMediaModal(false); setSelectedId(newItem.id);
                }} />

                <WebUrlDialog open={showWebModal} onClose={() => setShowWebModal(false)} onAdd={(url, name) => {
                    // Place on Top Layer (3)
                    const layer3Items = items.filter(i => i.layer === 3);
                    const lastEnd = layer3Items.reduce((max, i) => Math.max(max, i.start + i.duration), 0);
                    const startAt = layer3Items.length > 0 ? lastEnd : 0;

                    const newItem = {
                        id: Date.now(), type: 'web', src: url, name: name || 'Sitio Web',
                        // DEFAULT TO FULL SCREEN 100% W/H, Top Layer, Top Z-Index
                        start: startAt, duration: 15, layer: 3,
                        x: 0, y: 0, w: 100, h: 100, z: 3, visible: true, locked: false
                    };
                    setItems(prev => [...prev, newItem]); setShowWebModal(false); setSelectedId(newItem.id);
                }} />

                <PlaylistSelectDialog open={showPlaylistModal} onClose={() => setShowPlaylistModal(false)} currentLayoutId={layoutId} onSelect={(layout) => {
                    const layer0Items = items.filter(i => i.layer === 0);
                    const lastEnd = layer0Items.reduce((max, i) => Math.max(max, i.start + i.duration), 0);
                    
                    // We need a fake duration for editor purposes, assuming 60s natively if we can't compute it.
                    // Actually, let's parse zones to sum layer 0 items. 
                    let initialDuration = 0;
                    try {
                        const lZones = layout.zones && typeof layout.zones === 'string' ? JSON.parse(layout.zones) : (layout.zones || []);
                        initialDuration = lZones.filter(z => z.layer === 0).reduce((sum, z) => sum + (z.duration || 0), 0) || 60;
                    } catch (e) {
                        initialDuration = 60;
                    }

                    const newItem = {
                        id: Date.now(), type: 'playlist', src: layout.id, name: layout.name,
                        start: lastEnd, duration: initialDuration, layer: 0,
                        x: 0, y: 0, w: 100, h: 100, z: 0, visible: true, locked: false,
                        sourceDuration: initialDuration
                    };
                    setItems(prev => [...prev, newItem]); setShowPlaylistModal(false); setSelectedId(newItem.id);
                }} />

                <SettingsDialog
                    open={showSettingsModal}
                    onClose={() => setShowSettingsModal(false)}
                    resolution={resolution}
                    onSave={(newRes) => { setResolution(newRes); setShowSettingsModal(false); }}
                />
            </Box>
        </Box>
    );
};


function MediaSelectDialog({ open, onClose, onSelect }) {
    const [media, setMedia] = useState([]);
    useEffect(() => { if (open) axios.get(`${API_URL}/media`).then(res => setMedia(res.data)); }, [open]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { background: 'rgba(20, 20, 35, 0.95)', backdropFilter: 'blur(20px)' } }}>
            <DialogTitle sx={{ fontWeight: 800 }}>SELECCIONAR RECURSO</DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={2}>
                    {media.map(m => (
                        <Grid item xs={6} sm={4} md={3} key={m.id}>
                            <Card sx={{ cursor: 'pointer', '&:hover': { borderColor: '#3b82f6' }, border: '1px solid rgba(255,255,255,0.05)', bgcolor: 'rgba(255,255,255,0.02)' }} onClick={() => onSelect(m)}>
                                <Box sx={{ pt: '100%', position: 'relative', bgcolor: '#000' }}>
                                    {m.type === 'image' && (
                                        <CardMedia
                                            component="img"
                                            image={`${API_URL.replace('/api', '')}/uploads/${m.filename}`}
                                            sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                                        />
                                    )}
                                    {m.type === 'video' && (
                                        <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(59, 130, 246, 0.1)' }}>
                                            <PlayArrow sx={{ color: 'white', fontSize: 40, opacity: 0.5 }} />
                                        </Box>
                                    )}
                                </Box>
                                <Typography variant="caption" noWrap sx={{ display: 'block', p: 1, textAlign: 'center', fontWeight: 600 }}>{m.original_name}</Typography>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </DialogContent>
            <DialogActions><Button onClick={onClose}>Cancelar</Button></DialogActions>
        </Dialog>
    );
}

const NestedPlaylistPlayer = ({ item, currentTime, isPlaying }) => {
    const [nestedZones, setNestedZones] = useState((item.nestedZones && item.nestedZones.length > 0) ? item.nestedZones : null);
    const [loading, setLoading] = useState(!nestedZones);
    
    useEffect(() => {
        if (!nestedZones && loading) {
            axios.get(`${API_URL}/layouts/${item.src}`).then(res => {
                setNestedZones(res.data.zones || []);
                setLoading(false);
            }).catch(() => {
                setNestedZones([]);
                setLoading(false);
            });
        }
    }, [item.src, nestedZones, loading]);

    if (loading) return <Box sx={{ width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center', bgcolor:'#1e293b' }}><CircularProgress size={20} /></Box>;

    const localTime = currentTime - item.start;
    const activeNested = nestedZones.filter(i => 
        i.visible !== false && localTime >= i.start && localTime <= (i.start + (i.duration || 10))
    );

    return (
        <Box sx={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden', bgcolor: 'black', border: '1px dashed #3b82f6' }}>
            {activeNested.length === 0 && (
                <Box sx={{ width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', opacity: 0.5 }}>
                    <ViewList sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="caption">{item.name}</Typography>
                </Box>
            )}
            {activeNested.map(nItem => (
                <Box key={nItem.id} sx={{
                    position: 'absolute',
                    left: `${nItem.x}%`, top: `${nItem.y}%`,
                    width: `${nItem.w}%`, height: `${nItem.h}%`,
                    zIndex: nItem.z
                }}>
                    {nItem.type === 'video' && (
                        <video
                            src={`/uploads/${nItem.src}`}
                            style={{ width: '100%', height: '100%', objectFit: 'fill' }}
                            ref={el => {
                                if (el && isPlaying) {
                                    const offset = localTime - nItem.start;
                                    if (Math.abs(el.currentTime - offset) > 0.5) el.currentTime = Math.max(0, offset);
                                    el.play().catch(() => {});
                                } else if (el) { el.pause(); el.currentTime = Math.max(0, localTime - nItem.start); }
                            }}
                            muted
                        />
                    )}
                    {nItem.type === 'image' && <img src={`/uploads/${nItem.src}`} style={{ width: '100%', height: '100%', objectFit: 'fill' }} />}
                    {(nItem.type === 'html' || nItem.type === 'web') && <iframe src={nItem.src} style={{ width: '100%', height: '100%', border: 'none', pointerEvents: 'none' }} />}
                    {nItem.type === 'playlist' && (
                        <Box sx={{ width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center', bgcolor:'#1e293b' }}>
                            <ViewList sx={{ fontSize: 20 }} />
                        </Box>
                    )}
                </Box>
            ))}
        </Box>
    );
};

function WebUrlDialog({ open, onClose, onAdd }) {
    const [url, setUrl] = useState('');
    const [name, setName] = useState('');
    return (
        <Dialog open={open} onClose={onClose} fullWidth PaperProps={{ sx: { background: 'rgba(20, 20, 35, 0.95)', backdropFilter: 'blur(20px)' } }}>
            <DialogTitle>AÑADIR RECURSO WEB</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <TextField label="URL del sitio" fullWidth value={url} onChange={e => setUrl(e.target.value)} placeholder="https://..." />
                    <TextField label="Nombre (Opcional)" fullWidth value={name} onChange={e => setName(e.target.value)} />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>CANCELAR</Button>
                <Button variant="contained" onClick={() => onAdd(url, name)} disabled={!url}>AÑADIR</Button>
            </DialogActions>
        </Dialog>
    );
}

function SettingsDialog({ open, onClose, resolution, onSave }) {
    const [w, setW] = useState(resolution.width);
    const [h, setH] = useState(resolution.height);

    useEffect(() => {
        if (open) { setW(resolution.width); setH(resolution.height); }
    }, [open, resolution]);

    return (
        <Dialog open={open} onClose={onClose} PaperProps={{ sx: { background: 'rgba(20, 20, 35, 0.95)', backdropFilter: 'blur(20px)' } }}>
            <DialogTitle>CONFIGURACIÓN DE PROYECTO</DialogTitle>
            <DialogContent>
                <Typography variant="caption" sx={{ display: 'block', mb: 2, opacity: 0.7 }}>
                    Define la resolución base del lienzo. Los elementos se escalarán porcentualmente.
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={6}><TextField label="Ancho (px)" type="number" fullWidth value={w} onChange={e => setW(parseInt(e.target.value))} /></Grid>
                    <Grid item xs={6}><TextField label="Alto (px)" type="number" fullWidth value={h} onChange={e => setH(parseInt(e.target.value))} /></Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>CANCELAR</Button>
                <Button variant="contained" onClick={() => onSave({ width: w, height: h })}>APLICAR</Button>
            </DialogActions>
        </Dialog>
    );
}

export default TimelineEditor;

function PlaylistSelectDialog({ open, onClose, onSelect, currentLayoutId }) {
    const [layouts, setLayouts] = useState([]);
    useEffect(() => {
        if (open) {
            axios.get(`${API_URL}/layouts`).then(res => {
                setLayouts(res.data.filter(l => l.id.toString() !== currentLayoutId?.toString()));
            }).catch(console.error);
        }
    }, [open, currentLayoutId]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { background: 'rgba(20, 20, 35, 0.95)', backdropFilter: 'blur(20px)' } }}>
            <DialogTitle sx={{ fontWeight: 800 }}>SELECCIONAR PLAYLIST</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2}>
                    {layouts.length === 0 && <Typography variant="body2" sx={{ opacity: 0.5, textAlign: 'center' }}>No hay otras listas disponibles</Typography>}
                    {layouts.map(l => (
                        <Paper key={l.id} sx={{ p: 2, cursor: 'pointer', border: '1px solid rgba(255,255,255,0.05)', bgcolor: 'rgba(255,255,255,0.02)', '&:hover': { bgcolor: 'rgba(59, 130, 246, 0.1)', borderColor: '#3b82f6' } }} onClick={() => onSelect(l)}>
                            <Typography variant="body1" fontWeight={800}>{l.name}</Typography>
                        </Paper>
                    ))}
                </Stack>
            </DialogContent>
            <DialogActions><Button onClick={onClose}>Cancelar</Button></DialogActions>
        </Dialog>
    );
}
